import type { ComponentDiagnostic, TableColumn } from "@swot/base-components";
import { CmtsSmartDataGrid, type CmtsGridColumn, type CmtsGridRowAction } from "./data-grid/index.js";

export type CmtsColumnSpec<TRecord extends Record<string, unknown>> = {
  id?: keyof TRecord & string | undefined;
  key?: keyof TRecord & string | undefined;
  header?: string | undefined;
  label?: string | undefined;
  sortable?: boolean | undefined;
  filterable?: boolean | undefined;
  width?: number | string | undefined;
  align?: "left" | "center" | "right" | undefined;
  render?: TableColumn<TRecord>["render"] | undefined;
};

export type CmtsEnterpriseDataGridProps<TRecord extends Record<string, unknown>> = {
  title: string;
  subtitle?: string | undefined;
  rows?: readonly TRecord[] | null | undefined;
  columns: readonly CmtsColumnSpec<TRecord>[];
  rowKey?: keyof TRecord & string | undefined;
  loading?: boolean | undefined;
  error?: Error | string | null | undefined;
  diagnostics?: readonly ComponentDiagnostic[] | undefined;
  emptyMessage?: string | undefined;
  pageSize?: number | undefined;
  totalRowCount?: number | undefined;
  onRowClick?: ((record: TRecord) => void) | undefined;
  onExport?: ((rows: readonly TRecord[]) => void | Promise<void>) | undefined;
  actions?: readonly CmtsGridRowAction<TRecord>[] | undefined;
};

function normalizeColumns<TRecord extends Record<string, unknown>>(columns: readonly CmtsColumnSpec<TRecord>[]): readonly CmtsGridColumn<TRecord>[] {
  return columns.map((column) => {
    const id = (column.id ?? column.key) as keyof TRecord & string;
    return {
      id,
      header: column.header ?? column.label ?? id,
      sortable: column.sortable ?? true,
      filterable: column.filterable ?? true,
      width: column.width,
      align: column.align,
      render: column.render,
      emptyDisplay: "-",
    };
  }).filter((column) => Boolean(column.id));
}

export function CmtsEnterpriseDataGrid<TRecord extends Record<string, unknown>>(props: CmtsEnterpriseDataGridProps<TRecord>) {
  const safeRows = Array.isArray(props.rows) ? props.rows : [];
  const rowKey = props.rowKey ?? ("id" as keyof TRecord & string);
  return (
    <CmtsSmartDataGrid<TRecord>
      title={props.title}
      subtitle={props.subtitle}
      rows={safeRows}
      columns={normalizeColumns(props.columns)}
      rowKey={rowKey}
      loading={props.loading}
      error={props.error}
      emptyMessage={props.emptyMessage ?? "No rows were returned for the selected CMTS scope."}
      defaultPageSize={props.pageSize ?? 10}
      pageSizeOptions={[10, 25, 50, 100]}
      onRowClick={props.onRowClick}
      onExport={props.onExport}
      exportable
      refreshable
      actions={props.actions}
      developerMode={Boolean(props.diagnostics?.length)}
    />
  );
}
